<script lang="ts" setup>
import { addDays, format } from 'date-fns'
import type { Options } from 'vanilla-calendar-pro'
import { Calendar } from 'vanilla-calendar-pro'

import 'vanilla-calendar-pro/styles/index.css'
import constants from '~/constants'
import formatter from '~/helpers/formatter'
import type { DateTimeRange } from '~/types/date-time'

const props = withDefaults(
  defineProps<{
    defaultDateTimeRange?: DateTimeRange | null
    disabledDates?: string[]
    isRangeMode?: boolean
    onlyFutureDates?: boolean
    timeZone?: string
    withInput?: boolean
  }>(),
  {
    defaultDateTimeRange: null,
    disabledDates: [],
    isRangeMode: false,
    onlyFutureDates: false,
    timeZone: 'Europe/Moscow',
    withInput: true
  }
)

const emit = defineEmits<{
  (e: 'onClickArrow'): void
  (e: 'setDate', x: Date | null): void
  (e: 'setDateTimeRange', x: DateTimeRange | null): void
}>()

const dateMin = new Date()

if (props.onlyFutureDates) {
  dateMin.setDate(dateMin.getDate() + 1)
}

const disableDates = computed(() => props.disabledDates)

const config: Options = {
  ...(props.onlyFutureDates ? { dateMin } : {}),
  enableDateToggle: false,
  inputMode: true,
  locale: 'ru-RU',
  disableDates: disableDates.value,
  selectedDates: props.defaultDateTimeRange ? [
    props.defaultDateTimeRange.formattedDateTime1,
    props.defaultDateTimeRange.formattedDateTime2
  ] : [],
  selectionDatesMode: props.isRangeMode ? 'multiple-ranged' : 'single',
  onChangeToInput (self: Calendar): void {
    const { inputElement, selectedDates } = self.context

    if (!inputElement) {
      return
    }

    if (props.isRangeMode && selectedDates.length === 2 && selectedDates[0]) {
      dateTimeRange.value = {
        formattedDateTime1: `${selectedDates[0]}T00:00:00`,
        formattedDateTime2: `${format(addDays(selectedDates[1], 1), constants.dateTimeFormats.dateOutput)}T00:00:00`,
        timeZone: props.timeZone
      }
      inputElement.value = selectedDates[0] === selectedDates[1]
        ? formatter.formatDate(selectedDates[0])
        : `${formatter.formatDate(selectedDates[0])} — ${formatter.formatDate(selectedDates[1])}`

      self.hide()
    } else if (!props.isRangeMode && selectedDates.length === 1) {
      const selectedDate = new Date(selectedDates[0])

      date.value = selectedDate
      inputElement.value = formatter.formatDate(selectedDates[0])

      emit('setDate', selectedDate)

      self.hide()
    } else {
      dateTimeRange.value = null
      inputElement.value = ''
    }
  },
  onClickArrow (): void {
    emit('onClickArrow')
  }
}

const calendar = ref<Calendar>()
const calendarRef = ref<HTMLInputElement | null>(null)
const date = ref<Date | null>(null)
const dateTimeRange = ref<DateTimeRange | null>(null)

const placeholder = computed(() => props.isRangeMode ? 'Дата / диапазон дат (МСК)' : 'Дата (МСК)')

watch(dateTimeRange, () => {
  emit('setDateTimeRange', dateTimeRange.value)
})

watch(disableDates, (val) => {
    if (calendar.value) {
      calendar.value.set({
        disableDates: val,
        selectedMonth: calendar.value!.context.selectedMonth,
        selectedYear: calendar.value!.context.selectedYear,
      })
    }
}, {deep: true})

onMounted(() => {
  calendar.value = new Calendar(calendarRef.value!, config)
  calendar.value.init()

  if (props.defaultDateTimeRange) {
    const startDate = props.defaultDateTimeRange.formattedDateTime1
    const endDate = props.defaultDateTimeRange.formattedDateTime2
    calendar.value!.context.inputElement!.value = `${formatter.formatDate(startDate)} — ${formatter.formatDate(endDate)}`
  }
})

onUnmounted(() => {
  calendar.value?.destroy()
})

function clear () {
  calendar.value!.update()
  calendar.value!.context.inputElement!.value = ''
  dateTimeRange.value = null
}

defineExpose({ calendar, clear })
</script>

<template>
  <div class="relative rounded-xl bg-white">
    <SvgIcon
      v-if="withInput"
      class="pointer-events-none absolute left-4 top-3 text-2xl text-main-aluminum"
      name="outlined/calendar-today"
    />

    <input
      ref="calendarRef"
      class="
        box-border w-full appearance-none rounded-xl border border-solid border-main-aluminum py-[11px] pl-12 pr-4 leading-6
        hover:border-main-green focus:border-main-green focus-visible:outline-0
      "
      :class="{ 'pr-12': dateTimeRange, '!absolute !size-px !p-0 !opacity-0': !withInput }"
      :placeholder="placeholder"
      readonly
    >

    <SvgIcon
      v-if="withInput && dateTimeRange"
      class="absolute right-4 top-3 cursor-pointer text-2xl text-main-aluminum hover:text-main-red"
      name="outlined/close"
      @click="clear"
    />
  </div>
</template>
